import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../hooks/useCart";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  submitCheckout,
  verifyCheckoutPayment,
  validateCoupon,
  checkCheckoutEmail,
  cancelCheckout,
} from "../../config/api";
import {
  saveCheckoutPending,
  savePaymentResponse,
  clearCheckoutSession,
  getReusablePendingOrderId,
  getCheckoutUserKey,
  buildCartFingerprint,
} from "../../utils/checkoutRecovery";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import toast from "react-hot-toast";
import { normalizeEmail, isValidEmailFormat } from "../../utils/emailUtils";
import { getImageUrl } from "../../utils/helpers";
import {
  BsCreditCard,
  BsShieldCheck,
  BsLock,
  BsTruck,
  BsGift,
  BsCart,
  BsCheckAll,
} from "react-icons/bs";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaRupeeSign } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const MySwal = withReactContent(Swal);

const CheckoutPage = () => {
  const { user } = useAuth();
  const { cart, loading: cartLoading, clearCart, couponCode } = useCart();
  const navigate = useNavigate();

  // Get cart data from cart hook
  const [itemsTotal, setItemsTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [finalTotalPrice, setFinalTotalPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [couponError, setCouponError] = useState("");

  // Form state
  const [shippingDetails, setShippingDetails] = useState({
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
    companyName: "",
    country: "India",
    streetAddress: "",
    city: "",
    province: "Karnataka",
    postalCode: "",
    phone: "",
    email: user?.email || "",
    additionalInfo: "",
  });

  // Animation variants
  const containerVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, staggerChildren: 0.1 },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  const stepVariant = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
  };

  useEffect(() => {
    if (!user) return;

    setShippingDetails((prev) => ({
      ...prev,
      firstName: user.first_name || prev.firstName,
      lastName: user.last_name || prev.lastName,
      email: user.email || prev.email,
    }));
  }, [user]);

  useEffect(() => {
    if (!cart?.items?.length) return;

    const calculatedItemsTotal = cart.items.reduce(
      (sum, item) =>
        sum +
        (item.variant.price_sale || item.variant.price_regular || 0) *
          item.quantity,
      0,
    );
    setItemsTotal(calculatedItemsTotal);
    setTaxAmount(0);

    const calcDelivery = (subtotal) => (subtotal >= 2500 ? 0 : 80);

    if (!couponCode) {
      setCouponError("");
      setDiscount(0);
      setTotalPrice(calculatedItemsTotal);
      const del = calcDelivery(calculatedItemsTotal);
      setDeliveryCharge(del);
      setFinalTotalPrice(calculatedItemsTotal + del);
      return;
    }

    let cancelled = false;
    const orderItems = cart.items.map((item) => ({
      variant_id: item.variant.id,
      quantity: item.quantity || 1,
    }));

    (async () => {
      try {
        const res = await validateCoupon(couponCode, orderItems);
        if (cancelled) return;
        if (res?.valid) {
          setCouponError("");
          const disc = res.discountAmount || 0;
          const itemsAfter =
            res.itemsAfterDiscount ?? calculatedItemsTotal - disc;
          setDiscount(disc);
          setTotalPrice(itemsAfter);
          const del = calcDelivery(calculatedItemsTotal);
          setDeliveryCharge(del);
          setFinalTotalPrice(itemsAfter + del);
        } else {
          const message = res?.message || "Invalid coupon code";
          setCouponError(message);
          toast.error(message);
          setDiscount(0);
          setTotalPrice(calculatedItemsTotal);
          const del = calcDelivery(calculatedItemsTotal);
          setDeliveryCharge(del);
          setFinalTotalPrice(calculatedItemsTotal + del);
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err?.response?.data?.message || "Could not validate coupon";
          setCouponError(message);
          toast.error(message);
          setDiscount(0);
          setTotalPrice(calculatedItemsTotal);
          const del = calcDelivery(calculatedItemsTotal);
          setDeliveryCharge(del);
          setFinalTotalPrice(calculatedItemsTotal + del);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cart, couponCode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (user && name === "email") {
      return;
    }
    if (name === "phone") {
      const phoneNumber = value.replace(/\D/g, "");
      setShippingDetails((prev) => ({ ...prev, phone: phoneNumber }));
    } else {
      setShippingDetails((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    const requiredFields = [
      { name: "firstName", label: "First Name" },
      { name: "email", label: "Email Address" },
      { name: "streetAddress", label: "Street Address" },
      { name: "city", label: "Town / City" },
      { name: "province", label: "Province" },
      { name: "postalCode", label: "ZIP Code" },
      { name: "phone", label: "Phone" },
    ];

    const missingFields = requiredFields
      .filter(
        (field) =>
          !shippingDetails[field.name] ||
          shippingDetails[field.name].trim() === "",
      )
      .map((field) => field.label);

    if (missingFields.length > 0) {
      MySwal.fire({
        icon: "error",
        title: "Missing Required Fields",
        html: `<ul style="text-align: left;">${missingFields
          .map((field) => `<li>${field}</li>`)
          .join("")}</ul>`,
      });
      return false;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(shippingDetails.phone)) {
      MySwal.fire({
        icon: "error",
        title: "Invalid Phone Number",
        text: "Please enter a valid 10-digit phone number.",
      });
      return false;
    }

    const normalizedEmail = normalizeEmail(shippingDetails.email);
    if (!isValidEmailFormat(normalizedEmail)) {
      MySwal.fire({
        icon: "error",
        title: "Invalid Email Address",
        text: "Please enter a valid email address for order confirmation and tracking.",
      });
      return false;
    }

    if (user && !user.email) {
      MySwal.fire({
        icon: "error",
        title: "Account Email Missing",
        text: "Your account does not have an email on file. Please update your profile before checkout.",
      });
      return false;
    }

    return true;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        resolve(!!window.Razorpay);
      };
      script.onerror = () => {
        console.error("Failed to load Razorpay SDK");
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    if (!user) {
      const normalizedEmail = normalizeEmail(shippingDetails.email);
      try {
        const checkResult = await checkCheckoutEmail(normalizedEmail);
        const checkData = checkResult?.data || checkResult;

        if (checkData?.exists && !checkData?.isGuest) {
          const loginResult = await MySwal.fire({
            icon: "warning",
            title: "Account Already Exists",
            text:
              checkData.message ||
              "An account with this email already exists. Please log in to continue.",
            showCancelButton: true,
            confirmButtonText: "Go to Login",
            cancelButtonText: "Use Different Email",
          });

          if (loginResult.isConfirmed) {
            navigate("/login", {
              state: { from: "/checkout", email: normalizedEmail },
            });
          }
          return;
        }
      } catch (checkError) {
        console.error("Email check failed:", checkError);
        toast.error("Could not verify email. Please try again.");
        return;
      }
    }

    if (couponCode && couponError) {
      toast.error("Remove or fix the invalid coupon before paying");
      return;
    }

    if (!cart?.items || cart.items.length === 0) {
      MySwal.fire({
        icon: "warning",
        title: "Empty Cart",
        text: "Your cart is empty.",
      });
      return;
    }

    const outOfStock = cart.items.filter(
      (item) => item.quantity > (item.variant?.stock ?? 0),
    );
    if (outOfStock.length > 0) {
      MySwal.fire({
        icon: "warning",
        title: "Items Out of Stock",
        html: `<p>Some items exceed available stock. Please update your cart.</p><ul style="text-align:left">${outOfStock
          .map(
            (i) =>
              `<li>${i.variant?.product?.name || "Item"} (max ${i.variant?.stock ?? 0})</li>`,
          )
          .join("")}</ul>`,
      });
      return;
    }

    setIsLoading(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        MySwal.fire({
          icon: "error",
          title: "Payment Error",
          text: "Razorpay SDK failed to load. Please check your internet connection and try again.",
        });
        setIsLoading(false);
        return;
      }

      if (!process.env.REACT_APP_RAZORPAY_KEY) {
        MySwal.fire({
          icon: "error",
          title: "Configuration Error",
          text: "Razorpay payment gateway is not properly configured. Please contact support.",
        });
        setIsLoading(false);
        return;
      }

      const checkoutOrderItems = cart.items.map((item) => ({
        variant_id: item.variant.id,
        quantity: item.quantity || 1,
        product: item.variant.product?.id,
      }));

      const cartFingerprint = buildCartFingerprint(checkoutOrderItems);
      const checkoutUserKey = getCheckoutUserKey(user);
      const existingOrderId = getReusablePendingOrderId({
        cartFingerprint,
        couponCode,
        checkoutUserKey,
      });

      const checkoutPayload = {
        orderItems: checkoutOrderItems,
        shippingAddress: {
          address: shippingDetails.streetAddress,
          city: shippingDetails.city,
          state: shippingDetails.province,
          postalCode: shippingDetails.postalCode,
          country: shippingDetails.country,
        },
        customerDetails: {
          firstName: shippingDetails.firstName,
          lastName: shippingDetails.lastName,
          email: normalizeEmail(shippingDetails.email),
          phone: shippingDetails.phone,
        },
        ...(couponCode ? { couponCode } : {}),
        ...(existingOrderId ? { existingOrderId } : {}),
      };

      const checkoutResult = await submitCheckout(checkoutPayload);

      if (!checkoutResult?.success || !checkoutResult?.data) {
        throw new Error(checkoutResult?.message || "Failed to start checkout");
      }

      const {
        orderId,
        orderNumber,
        trackingToken,
        razorpayOrderId,
        razorpayKeyId,
        amount,
        currency,
      } = checkoutResult.data;

      saveCheckoutPending({
        orderId,
        trackingToken,
        razorpayOrderId,
        couponCode: couponCode || null,
        cartFingerprint,
        checkoutUserKey,
      });

      const options = {
        key: razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY,
        amount,
        currency: currency || "INR",
        name: "Knots & Krafts",
        description: `Order ${orderNumber}`,
        order_id: razorpayOrderId,
        handler: async (response) => {
          try {
            if (
              !response?.razorpay_payment_id ||
              !response?.razorpay_order_id ||
              !response?.razorpay_signature
            ) {
              throw new Error("Invalid payment response");
            }

            // Persist before verify so crash/network loss can recover on pending page
            savePaymentResponse({
              trackingToken,
              orderId,
              orderNumber,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            await verifyCheckoutPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            clearCheckoutSession();
            await clearCart();

            navigate(
              `/checkout/success?token=${trackingToken}&order=${encodeURIComponent(orderNumber)}`,
            );
          } catch (verifyError) {
            console.error("Verify error after payment:", verifyError);
            navigate(`/checkout/pending?token=${trackingToken}`);
          } finally {
            setIsLoading(false);
          }
        },
        prefill: {
          name: `${shippingDetails.firstName} ${shippingDetails.lastName}`,
          email: normalizeEmail(shippingDetails.email),
          contact: shippingDetails.phone,
        },
        notes: {
          address: shippingDetails.streetAddress,
        },
        theme: {
          color: "#FBBF24",
        },
        modal: {
          ondismiss: () => {
            setIsLoading(false);
            if (trackingToken) {
              cancelCheckout({ orderToken: trackingToken }).catch(() => {});
            }
            clearCheckoutSession();
            navigate(
              `/checkout/failure?token=${trackingToken || ""}&reason=cancelled`,
            );
          },
        },
      };

      const paymentObject = new window.Razorpay(options);

      paymentObject.on("payment.failed", () => {
        setIsLoading(false);
        if (trackingToken) {
          cancelCheckout({ orderToken: trackingToken }).catch(() => {});
        }
        clearCheckoutSession();
        navigate(
          `/checkout/failure?token=${trackingToken || ""}&reason=failed`,
        );
      });

      paymentObject.open();
    } catch (error) {
      console.error("Payment error:", error);
      const refreshError =
        error.response?.data?.message ===
        "Only pending orders can be refreshed";
      if (refreshError) {
        clearCheckoutSession();
      }
      MySwal.fire({
        icon: "error",
        title: "Checkout Error",
        text:
          error.response?.data?.message ||
          error.message ||
          "There was an error starting checkout. Please try again.",
      });
      setIsLoading(false);
    }
  };

  // Show loading while cart is being fetched
  if (cartLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your cart...</p>
        </div>
      </div>
    );
  }

  // Show empty cart message if no items
  if (!cart?.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <BsCart className="text-6xl text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Your cart is empty
          </h2>
          <p className="text-gray-600 mb-6">
            Add some items to your cart to proceed with checkout.
          </p>
          <button
            onClick={() => navigate("/shop")}
            className="bg-yellow-500 text-white px-6 py-3 rounded-lg hover:bg-yellow-600 transition-colors"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <Helmet>
        <title>Checkout - Knots & Krafts</title>
        <meta
          name="description"
          content="Complete your purchase securely with multiple payment options"
        />
      </Helmet>

      {/* Hero Section */}
      <motion.div
        className="relative bg-primary-600 h-64 flex flex-col justify-center items-center text-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <motion.div
          className="relative z-10"
          variants={containerVariant}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariant} className="mb-4">
            <BsCheckAll size={48} className="text-white inline-block" />
          </motion.div>
          <motion.h1
            variants={itemVariant}
            className="text-4xl font-bold text-white mb-2"
          >
            Secure Checkout
          </motion.h1>
          <motion.div variants={itemVariant} className="text-white/90">
            <span
              className="hover:underline cursor-pointer"
              onClick={() => navigate("/")}
            >
              Home
            </span>
            {" > "}
            <span>Checkout</span>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="mb-4 flex justify-center"
              >
                <AiOutlineLoading3Quarters
                  size={48}
                  className="text-yellow-500"
                />
              </motion.div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Processing Your Order
              </h3>
              <p className="text-gray-600">
                Please wait while we securely process your payment...
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.div
        className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8"
        variants={containerVariant}
        initial="hidden"
        animate="visible"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Billing Details */}
          <motion.div className="lg:col-span-2" variants={itemVariant}>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <BsShieldCheck className="mr-3 text-green-500" />
                Billing Details
              </h2>

              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <motion.div whileFocus={{ scale: 1.02 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={shippingDetails.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                      required
                    />
                  </motion.div>

                  <motion.div whileFocus={{ scale: 1.02 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={shippingDetails.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                    />
                  </motion.div>
                </div>

                <motion.div whileFocus={{ scale: user ? 1 : 1.02 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={shippingDetails.email}
                    onChange={handleInputChange}
                    readOnly={Boolean(user)}
                    disabled={Boolean(user)}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 ${
                      user ? "bg-gray-100 text-gray-700 cursor-not-allowed" : ""
                    }`}
                    required
                  />
                  {user ? (
                    <p className="mt-1 text-xs text-gray-500">
                      Using your account email for order updates.
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      Required for order confirmation and tracking.
                    </p>
                  )}
                </motion.div>

                <motion.div whileFocus={{ scale: 1.02 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    maxLength={10}
                    value={shippingDetails.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                    required
                  />
                </motion.div>

                <motion.div whileFocus={{ scale: 1.02 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    name="streetAddress"
                    value={shippingDetails.streetAddress}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                    required
                  />
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <motion.div whileFocus={{ scale: 1.02 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={shippingDetails.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                      required
                    />
                  </motion.div>

                  <motion.div whileFocus={{ scale: 1.02 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={shippingDetails.postalCode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                      required
                    />
                  </motion.div>
                </div>

                <motion.div whileFocus={{ scale: 1.02 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State/Province *
                  </label>
                  <select
                    name="province"
                    value={shippingDetails.province}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                    required
                  >
                    <option value="Karnataka">Karnataka</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Assam">Assam</option>
                    <option value="Meghalaya">Meghalaya</option>
                    <option value="Manipur">Manipur</option>
                    <option value="Mizoram">Mizoram</option>
                    <option value="Nagaland">Nagaland</option>
                    <option value="Tripura">Tripura</option>
                    <option value="Sikkim">Sikkim</option>
                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                    <option value="Uttarakhand">Uttarakhand</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Chhattisgarh">Chhattisgarh</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Goa">Goa</option>
                  </select>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div className="lg:col-span-1" variants={itemVariant}>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sticky top-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <BsGift className="mr-3 text-yellow-500" />
                Order Summary
              </h2>

              {/* Cart Items */}
              <div className="space-y-4 mb-6">
                {cart?.items?.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <img
                      src={getImageUrl(
                        item.variant.images?.[0]?.url ||
                          item.variant.product.images?.[0]?.url,
                      )}
                      alt={item.variant.name || item.variant.product.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm">
                        {item.variant.name || item.variant.product.name}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-black flex items-center">
                        <FaRupeeSign className="w-3 h-3 mr-1 text-black" />
                        {(
                          (item.variant.price_sale ||
                            item.variant.price_regular ||
                            0) * item.quantity
                        ).toFixed(2)}
                      </p>
                    </div>
                  </motion.div>
                )) || []}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 border-t border-gray-200 pt-6">
                <div className="flex justify-between text-black">
                  <span>Subtotal</span>
                  <span className="flex items-center text-black">
                    <FaRupeeSign className="w-3 h-3 mr-1 text-black" />
                    {itemsTotal.toFixed(2)}
                  </span>
                </div>

                {couponError && (
                  <p className="text-sm text-red-600">{couponError}</p>
                )}

                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount{couponCode ? ` (${couponCode})` : ""}</span>
                    <span className="flex items-center">
                      -<FaRupeeSign className="w-3 h-3 mr-1 text-black" />
                      {discount.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between text-black">
                  <span>Delivery Charges</span>
                  <span className="flex items-center text-black">
                    {deliveryCharge === 0 ? (
                      <span className="text-green-600 font-medium">FREE</span>
                    ) : (
                      <>
                        <FaRupeeSign className="w-3 h-3 mr-1 text-black" />
                        {deliveryCharge.toFixed(2)}
                      </>
                    )}
                  </span>
                </div>

                <div className="flex justify-between text-xl font-bold text-black border-t border-black pt-3">
                  <span>Total</span>
                  <span className="flex items-center text-black">
                    <FaRupeeSign className="w-4 h-4 mr-1 text-black" />
                    {finalTotalPrice.toFixed(2)}
                  </span>
                </div>
                <div className="text-center mt-2">
                  <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                    Inclusive of all taxes
                  </span>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BsLock className="mr-2 text-green-500" />
                  Payment Method
                </h3>

                <div className="space-y-3">
                  <motion.label
                    className="flex items-center p-4 border-2 border-black rounded-xl cursor-pointer transition-all duration-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="Razorpay"
                      checked={true}
                      readOnly
                      className="sr-only"
                    />
                    <div className="w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center border-black bg-black">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                    <BsCreditCard className="mr-3 text-blue-500" />
                    <span className="font-medium">
                      Online Payment (Razorpay)
                    </span>
                  </motion.label>
                </div>
              </div>

              {/* Security Notice */}
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start">
                  <BsShieldCheck className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-green-700">
                    Your payment information is secure and encrypted. We never
                    store your card details. All transactions are processed
                    securely through Razorpay.
                  </p>
                </div>
              </div>

              {/* Place Order Button */}
              <motion.button
                onClick={handlePayment}
                disabled={isLoading || Boolean(couponCode && couponError)}
                className="w-full mt-8 bg-black text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:from-yellow-500 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <BsCreditCard className="mr-2" />
                    <span className="flex items-center">
                      Pay Online -
                      <FaRupeeSign className="w-3 h-3 mx-1" />
                      {finalTotalPrice.toFixed(2)}
                    </span>
                  </>
                )}
              </motion.button>

              {/* Free Shipping Notice */}
              <motion.div
                className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-sm text-green-700 text-center font-medium flex items-center justify-center gap-1">
                  <BsGift className="flex-shrink-0" />
                  Free shipping on orders above
                  <FaRupeeSign className="w-3 h-3" />
                  2500!
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default CheckoutPage;
